<template>
  <div class="table-responsive">
    <table id="notifications-table" class="table table-striped">
      <thead>
        <tr>
          <th class="notification-name">{{$t('profile.notifications.notification')}}</th>
          <th>{{$t('profile.notifications.subscribed_entities')}}</th>
          <th class="notification-actions"></th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="(notification, i) in filteredNotifications" :key="i">
          <td>
            {{$t(`profile.notifications.types.${notification.type}`)}}
          </td>
          <td>
            <div v-if="updateIndex == i">
              <div class="form-group" style="margin-bottom:0rem;">
                <div class="input-group clearable-filter">
                  <multiselect
                    v-model="subscribedEntities"
                    :options="entitiesAvailableForSubscription"
                    track-by="id"
                    label="name"
                    deselect-label="×"
                    :placeholder="$t('profile.notifications.no_entity_selected')"
                    :allow-empty="true"
                    :multiple="true"
                    :searchable="true"
                    :close-on-select="false"
                    :limit="previewEntitiesLimit"
                    :limitText="multiselectLimitText"
                    open-direction="bottom"
                    >

                    <template slot="tag" slot-scope="{ option, remove }">
                      <span class="custom__tag">
                        <span>{{ formatedEntityLabel(option) }}&nbsp;
                        </span>
                        <i @click="remove(option)" aria-hidden="true" tabindex="1" class="custom__tag-icon"></i>
                      </span>
                    </template>
                    <template slot="option" slot-scope="{ option }">
                      <span class="option__name">
                        {{ formatedEntityLabel(option) }}
                      </span>
                    </template>
                    <template slot="clear">
                      <div class="multiselect__clear" v-if="subscribedEntities.length" @mousedown.prevent.stop="subscribedEntities = []"></div>
                    </template>
                    <span slot="noResult">{{$t('profile.notifications.multiselect_no_results')}}</span>
                  </multiselect>
                </div>
              </div>
            </div>

            <div v-else>
              <div v-if="entitiesSubscribedByNotification[notification.id].length > 0" class="row align-items-center flex-wrap">
                <span v-for="(entity, j) in entitiesSubscribedByNotification[notification.id].slice(0, previewEntitiesLimit)" :key="j"
                    class="custom-tag my-1"
                  >
                {{entity.name}}
                </span>
                <span v-if="entitiesSubscribedByNotification[notification.id].length - previewEntitiesLimit > 0"
                    class="custom-tag my-1"
                  >
                  {{$t('profile.notifications.more_entities', {number: entitiesSubscribedByNotification[notification.id].length - previewEntitiesLimit})}}
                </span>
              </div>
              <div v-else>
                  {{$t('profile.notifications.no_subscribed_entities')}}
              </div>
            </div>
          </td>

          <td>
            <div class="d-flex justify-content-end pr-3">
              <va-popover
                :message="$t('profile.notifications.update_subscriptions')"
                placement="left"
              >
                <i v-show="updateIndex == null && canUpdateNotificationSubscriptions(notification)" class="fa fa-pencil green-icon" @click="setupUpdate(i)"></i>
              </va-popover>
              <va-popover
                :message="$t('profile.notifications.confirm_update_subscriptions')"
                placement="left"
              >
                <i v-show="updateIndex !== null && updateIndex === i " class="fa fa-check px-2 green-icon" @click="confirmUpdate"></i>
              </va-popover>
              <va-popover
                :message="$t('profile.notifications.cancel')"
                placement="left"
              >
                <i v-show="updateIndex !== null && updateIndex === i" class="fa fa-times px-2 green-icon" @click="cancelUpdate"></i>
              </va-popover>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

  </div>
</template>

<script>

import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'
import Multiselect from 'vue-multiselect'
import { UserNotifications } from '@/api/Notifications'

export default {
  props: {
    notifications: {
      type: Array,
      required: true
    },
    userNotifications: {
      type: Array,
      required: true
    },
    search: {
      type: String,
      required: true
    }
  },
  mixins: [
    MultiselectLabelFormatter
  ],
  components: {
    Multiselect
  },
  mounted () {
    this.$emit('finishedMount')
  },
  data: function () {
    return {
      previewEntitiesLimit: 4,
      updateIndex: null,
      entitiesAvailableForSubscription: [],
      subscribedEntities: [],
    }
  },
  computed: {
    entitiesSubscribedByNotification: function () {
      let entitiesByNotification = {}
      if (this.notifications.length > 0) {
        this.notifications.forEach(notification => {
          entitiesByNotification[notification.id] = this.userNotifications
            .filter(userNotification => userNotification.notification_id === notification.id)
            .map(userNotification =>
              this.globalAuthService.entities
                .find(entity => entity.id === userNotification.entity_id))
            .filter(entity => entity)
        })
      }
      return entitiesByNotification
    },
    entitiesAvailableForSubscriptionByNotification: function () {
      let entitiesByNotification = {}
      if (this.notifications.length > 0) {
        this.notifications.forEach(notification => {
          entitiesByNotification[notification.id] = []

          let userPermissions = this.globalAuthService.user_permissions
            .filter(permission =>
              permission.info_type === notification.info_type.type &&
            permission.service_id === notification.info_type.service_id &&
            permission.access_type >= notification.access_type)

          if (userPermissions.length > 0) {
            if (userPermissions.some(permission => permission.entity_id === null)) {
              entitiesByNotification[notification.id] = [...this.globalAuthService.entities]
            } else {
              userPermissions.forEach(permission => {
                entitiesByNotification[notification.id]
                  .push(this.globalAuthService.entities
                    .find(entity => entity.id === permission.entity_id))
              })
            }
          }
        })
      }
      return entitiesByNotification
    },
    filteredNotifications: function () {
      let notifications = this.notifications
        .filter(notification =>
          this.entitiesAvailableForSubscriptionByNotification[notification.id].length > 0)

      if (this.search.trim()) {
        let lowercaseSearch = this.search.toLowerCase()
        notifications = notifications
          .filter(notification => this.$t(`profile.notifications.types.${notification.type}`)
            .toLowerCase()
            .includes(lowercaseSearch))
      }

      return notifications
    },
    globalAuthService: function () {
      return this.$store.getters.globalAuthService
    },
  },
  methods: {
    setupUpdate: function (index) {
      this.updateIndex = index
      let notification = this.filteredNotifications[index]
      this.entitiesAvailableForSubscription = this.entitiesAvailableForSubscriptionByNotification[notification.id]
      this.subscribedEntities = this.entitiesSubscribedByNotification[notification.id]
    },
    cancelUpdate: function () {
      this.updateIndex = null
      this.subscribedEntities = []
      this.entitiesAvailableForSubscription = []
    },
    confirmUpdate: function () {
      let notification = this.filteredNotifications[this.updateIndex]

      let data = {
        notification_id: notification.id,
        entities: this.subscribedEntities.map(entity => entity.id)
      }

      //let userId = this.$auth.user().id
      let userId = this.$route.params.id
      UserNotifications.update(userId, data)
        .then(userNotifications => {
          this.$emit('updatedUserNotifications', userNotifications)
          this.cancelUpdate()
        })
        .catch(() => {
          this.showToast(this.$t('profile.notifications.something_went_wrong'), this.$store.state.errorToastOptions)
        })
    },
    canUpdateNotificationSubscriptions: function (notification) {
      return this.entitiesAvailableForSubscriptionByNotification[notification.id].length > 0
    },
    multiselectLimitText: function (count) {
      return this.$t('profile.notifications.multiselect_limit_text', { number: count })
    }
  }
}
</script>
